import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faArrowLeft, faBook } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import matrizCurricularService from '../../../services/matrizCurricularService';
import cursoService from '../../../services/cursoService';

const MatrizCurricularForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [cursos, setCursos] = useState([]);
  const [formData, setFormData] = useState({
    idCurso: '',
    ano: new Date().getFullYear(),
    nome: '',
    vigenciaInicio: '',
    vigenciaFim: '',
    observacoes: ''
  });

  const [errors, setErrors] = useState({});

  // Carregar cursos
  useEffect(() => {
    const loadCursos = async () => {
      try {
        const response = await cursoService.list({ page: 1, limit: 1000 });
        if (response.success) {
          setCursos(response.data);
        }
      } catch (err) {
        console.error('Erro ao carregar cursos:', err);
        toast.error('Erro ao carregar lista de cursos');
      }
    };
    loadCursos();
  }, []);

  // Carregar dados da matriz se estiver editando
  useEffect(() => {
    if (isEditing) {
      loadMatriz();
    }
  }, [id]);

  const loadMatriz = async () => {
    try {
      setLoading(true);
      const response = await matrizCurricularService.getById(id);
      
      if (response.success) {
        const matriz = response.data;
        
        const formDataToSet = {
          idCurso: matriz.idCurso || '',
          ano: matriz.ano || new Date().getFullYear(),
          nome: matriz.nome || '',
          vigenciaInicio: matriz.vigenciaInicio ? matriz.vigenciaInicio.split('T')[0] : '',
          vigenciaFim: matriz.vigenciaFim ? matriz.vigenciaFim.split('T')[0] : '',
          observacoes: matriz.observacoes || ''
        };
        
        setFormData(formDataToSet);
      } else {
        toast.error('Erro ao carregar dados da matriz');
        navigate('/academico/matriz-curricular');
      }
    } catch (err) {
      console.error('Erro ao carregar matriz:', err);
      toast.error(err.message || 'Erro ao carregar dados da matriz');
      navigate('/academico/matriz-curricular');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Limpar erro do campo
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.idCurso) {
      newErrors.idCurso = 'Curso é obrigatório';
    }

    if (!formData.ano) {
      newErrors.ano = 'Ano é obrigatório';
    } else if (formData.ano < 2000 || formData.ano > 2100) {
      newErrors.ano = 'Ano deve estar entre 2000 e 2100';
    }

    if (!formData.nome || formData.nome.trim() === '') {
      newErrors.nome = 'Nome da matriz é obrigatório';
    }

    if (formData.vigenciaInicio && formData.vigenciaFim) {
      const inicio = new Date(formData.vigenciaInicio);
      const fim = new Date(formData.vigenciaFim);
      
      if (fim < inicio) {
        newErrors.vigenciaFim = 'Data fim deve ser posterior à data início';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    try {
      setLoading(true);

      const dataToSend = {
        nome: formData.nome,
        idCurso: formData.idCurso,
        ano: formData.ano,
        dataVigenciaInicio: formData.vigenciaInicio || null,
        dataVigenciaFim: formData.vigenciaFim || null,
        observacoes: formData.observacoes || null
      };

      let response;
      if (isEditing) {
        response = await matrizCurricularService.update(id, dataToSend);
      } else {
        response = await matrizCurricularService.create(dataToSend);
      }

      if (response.success) {
        toast.success(isEditing ? 'Matriz atualizada com sucesso!' : 'Matriz criada com sucesso!');
        navigate('/academico/matriz-curricular');
      } else {
        toast.error(response.message || 'Erro ao salvar matriz');
      }
    } catch (err) {
      console.error('Erro ao salvar matriz:', err);
      toast.error(err.message || 'Erro ao salvar matriz');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 w-full max-w-full overflow-x-hidden">
      <div className="px-2 sm:px-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate('/academico/matriz-curricular')}
              className="text-gray-600 hover:text-gray-800"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="text-xl" />
            </button>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <FontAwesomeIcon icon={faBook} className="text-blue-600" />
              {isEditing ? 'Editar Matriz Curricular' : 'Nova Matriz Curricular'}
            </h1>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Curso */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Curso <span className="text-red-500">*</span>
                </label>
                <select
                  name="idCurso"
                  value={formData.idCurso}
                  onChange={handleChange}
                  disabled={loading}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.idCurso ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Selecione um curso</option>
                  {cursos.map(curso => (
                    <option key={curso.idCurso} value={curso.idCurso}>
                      {curso.nome}
                    </option>
                  ))}
                </select>
                {errors.idCurso && (
                  <p className="text-red-500 text-sm mt-1">{errors.idCurso}</p>
                )}
              </div>

              {/* Ano */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ano <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="ano"
                  value={formData.ano}
                  onChange={handleChange}
                  disabled={loading}
                  min="2000"
                  max="2100"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.ano ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.ano && (
                  <p className="text-red-500 text-sm mt-1">{errors.ano}</p>
                )}
              </div>

              {/* Nome */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome da Matriz <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Ex: Matriz Curricular 2024 - Curso Básico de Teologia"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.nome ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.nome && (
                  <p className="text-red-500 text-sm mt-1">{errors.nome}</p>
                )}
              </div>

              {/* Vigência Início */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vigência Início
                </label>
                <input
                  type="date"
                  name="vigenciaInicio"
                  value={formData.vigenciaInicio}
                  onChange={handleChange}
                  disabled={loading}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.vigenciaInicio ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.vigenciaInicio && (
                  <p className="text-red-500 text-sm mt-1">{errors.vigenciaInicio}</p>
                )}
              </div>

              {/* Vigência Fim */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vigência Fim
                </label>
                <input
                  type="date"
                  name="vigenciaFim"
                  value={formData.vigenciaFim}
                  onChange={handleChange}
                  disabled={loading}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.vigenciaFim ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.vigenciaFim && (
                  <p className="text-red-500 text-sm mt-1">{errors.vigenciaFim}</p>
                )}
              </div>

              {/* Observações */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Observações
                </label>
                <textarea
                  name="observacoes"
                  value={formData.observacoes}
                  onChange={handleChange}
                  disabled={loading}
                  rows="4"
                  placeholder="Observações sobre esta matriz curricular..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Botões */}
            <div className="flex gap-4 mt-6">
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faSave} />
                {loading ? 'Salvando...' : 'Salvar'}
              </button>

              <button
                type="button"
                onClick={() => navigate('/academico/matriz-curricular')}
                disabled={loading}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        </form>

        {/* Informação adicional */}
        {!isEditing && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
            <p className="text-blue-800 text-sm">
              <strong>Após criar a matriz:</strong> Você poderá adicionar módulos e disciplinas na tela de edição.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MatrizCurricularForm;
